/**
 * 主軸カテゴリ (product_categories) の hooks。
 * v3.1.3 — 商談ボード「カテゴリー管理」タブで使用。
 */
import type {
  CategoryBankAccount,
  CategoryLogoPosition,
  CompanyInfo,
  ProductCategory,
  ProductCategoryKind,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useMemo } from 'react';

import { db, functions } from '@/lib/firebase';

export interface ProductCategoryRecord extends ProductCategory {
  id: string;
}

/**
 * 全カテゴリを取得 (active な _deleted=false のみ)。
 * displayOrder 昇順。
 */
export function useProductCategories(opts?: { includeInactive?: boolean }) {
  const includeInactive = opts?.includeInactive ?? false;
  return useQuery({
    queryKey: ['product_categories', { includeInactive }],
    queryFn: async (): Promise<ProductCategoryRecord[]> => {
      const constraints = [where('_deleted', '==', false), orderBy('displayOrder', 'asc')];
      const snap = await getDocs(
        query(collection(db, 'product_categories'), ...constraints),
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as ProductCategory) }));
      return includeInactive ? all : all.filter((c) => c.isActive !== false);
    },
  });
}

/** Map<id, name> を提供。DealCard などの動的表示で使用。 */
export function useProductCategoryNameMap() {
  const { data } = useProductCategories({ includeInactive: true });
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const c of data ?? []) m.set(c.id, c.name);
    return m;
  }, [data]);
}

const upsertFn = httpsCallable<
  {
    id?: string;
    name: string;
    description?: string | null;
    color?: string | null;
    displayOrder?: number;
    isActive?: boolean;
    kind?: ProductCategoryKind | null;
    logoStoragePath?: string | null;
    logoPosition?: CategoryLogoPosition | null;
    /** カテゴリ別の自動メール送信元 mail_account ID（null = 営業設定既定にフォールバック）。 */
    mailAccountId?: string | null;
    /** @deprecated v3.2.21 以降は linkedBrandNames を使う。後方互換のため残置。 */
    catalogIds?: string[];
    /** v3.2.21: 紐付けブランド名一覧。各ブランドの「適用中」カタログから商品が自動引き当てされる。 */
    linkedBrandNames?: string[];
    /** v3.2.25: 同意ステップ (成約 → 同意 → 入金) を経由するか。 */
    requiresConsent?: boolean;
    /** v3.2.28: メーカー (発注先) メールアドレス（単一・後方互換）。空文字/null でクリア。 */
    manufacturerEmail?: string | null;
    /** v3.3.x: メーカー (発注先) メールアドレス（複数, To）。[]/null でクリア。 */
    manufacturerEmails?: string[] | null;
    /** v3.3.x: メーカー宛メールの既定 CC（複数）。[]/null でクリア。 */
    manufacturerCcEmails?: string[] | null;
    /** v3.3.x: メーカー取引通貨 ISO コード (例: 'EUR')。null/'' で既定(EUR)。 */
    manufacturerCurrency?: string | null;
    /** v3.3.x: カテゴリー別の請求書振込先口座。null でクリア (オーナー既定にフォールバック)。 */
    bankAccount?: CategoryBankAccount | null;
    /** v3.3.x: カテゴリー別の発行元会社情報。null でクリア (デフォルトにフォールバック)。 */
    companyInfo?: CompanyInfo | null;
  },
  { id: string; renamedDeals: number; renamedForms: number }
>(functions, 'upsertProductCategory');

export function useUpsertProductCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof upsertFn>[0]) => {
      const r = await upsertFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product_categories'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['forms'] });
    },
  });
}

const deleteFn = httpsCallable<
  { id: string },
  { id: string; referencedDeals: number; referencedForms: number }
>(functions, 'deleteProductCategory');

export function useDeleteProductCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const r = await deleteFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product_categories'] });
    },
  });
}
